import {
  isOrderEditable,
  WindowAddress,
} from '@cutr/constants/windows/windows';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AddressInfo } from '@/blocks/AddressInfo';
import Card from '@/blocks/Card';
import { Button, RouterButton } from '@/primitives/Button';
import { Field, FieldProp } from '@/primitives/Field';
import { ArrowLeft, Cart, Icon, UserAvatar } from '@/primitives/Icons';
import { Select } from '@/primitives/Select';
import { CountryCode, getCountriesList } from '@/utils/intl';
import { OrderTitle } from '@/windows/blocks/OrderTitle';
import { QuoteSummary } from '@/windows/blocks/QuoteSummary';
import {
  DeliveryAddressProvider,
  useAddressPretty,
  useDeliveryAddress,
  WindowAddressState,
} from '@/windows/interfaces/AddressContext';

import { useWindowLoaderData } from '../api/windowStore';
import { usePlaceOrder, useUpdateDeliveryAddress } from '../queries/order/crud';
import styles from './ResellerOrderCheckout.module.css';

export const ResellerOrderCheckout = () => {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const order = useWindowLoaderData();
  const [isAddressEditing, setIsAddressEditing] = useState(false);
  const navigate = useNavigate();
  const { mutateAsync: placeOrder } = usePlaceOrder();

  if (!orderId) {
    return null;
  }

  const isEditable = isOrderEditable(order.status);

  return (
    <div className={styles.content}>
      <OrderTitle readonly={!isEditable} />
      <div
        className="flexAlign"
        style={{ gap: 'var(--space-l)', paddingBottom: 'var(--space-m)' }}
      >
        <RouterButton
          to={`/windows/orders/${orderId}/review`}
          icon={<Icon icon={<ArrowLeft />} />}
          variant="secondary"
        >
          {t('reseller.cta.backToQuote')}
        </RouterButton>
        <h3>{t('reseller.orderReview.title.checkout')}</h3>
      </div>

      <section className="positionConfigurationLayout">
        <main>
          <div className={styles.orderInfo}>
            <OwnerBlock {...order.userLead} />
            <DeliveryAddressProvider initialAddress={order.deliveryAddress}>
              <DeliveryAddressBlock
                isAddressEditing={isAddressEditing}
                setIsAddressEditing={setIsAddressEditing}
              />
            </DeliveryAddressProvider>
          </div>
        </main>
        <aside className={styles.cardContainer}>
          <QuoteSummary order={order}>
            <Button
              icon={<Icon icon={<Cart />} />}
              className="full"
              onClick={async () => {
                await placeOrder(orderId);
                navigate(`/windows/orders/${orderId}/review`);
              }}
            >
              {t('reseller.cta.confirmOrder')}
            </Button>
          </QuoteSummary>
        </aside>
      </section>
    </div>
  );
};

const OwnerBlock = ({ email, name }: { email: string; name: string }) => {
  const { t } = useTranslation();
  return (
    <div className={'opposites'}>
      <h3>{t('reseller.orderReview.checkout.orderOwner')}</h3>
      <div className={styles.blockInfo}>
        <Card className={styles.cardContainer}>
          <div>
            <div className="opposites">
              <h4 className={styles.title}>
                <Icon icon={<UserAvatar />} size={2.5} />
                {name}
              </h4>
            </div>
            <p>{email}</p>
          </div>
        </Card>
      </div>
    </div>
  );
};

const DeliveryAddressBlock = ({
  isAddressEditing,
  setIsAddressEditing,
}: {
  isAddressEditing: boolean;
  setIsAddressEditing: (value: boolean) => void;
}) => {
  const addressPretty = useAddressPretty();
  const { t } = useTranslation();
  const { address, init } = useDeliveryAddress();
  const { mutateAsync: updateAddress, isPending } = useUpdateDeliveryAddress();

  const [initialAddress, setInitialAddress] = useState(address);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await updateAddress({
        address: address as unknown as WindowAddress,
      });
      toast.success(t('reseller.orderReview.checkout.deliveryAddressUpdated'));
      setInitialAddress(address);
    } catch (error) {
      toast.error(
        t('reseller.orderReview.checkout.deliveryAddressUpdateError')
      );
    } finally {
      setIsAddressEditing(false);
    }
  };

  return (
    <div className={'opposites'}>
      <h3>{t('reseller.orderReview.checkout.deliveryDetails')}</h3>
      <div className={styles.blockInfo}>
        {isAddressEditing ? (
          <Card className={styles.cardContainer}>
            <form onSubmit={handleSave}>
              <DeliveryAddressForm />
              <div
                style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'flex-end',
                  marginTop: '-1rem',
                }}
              >
                <Button type="submit" variant="straight" disabled={isPending}>
                  {t('common.cta.save')}
                </Button>

                <Button
                  variant="secondary"
                  disabled={isPending}
                  onClick={() => {
                    init(initialAddress as WindowAddressState);
                    setIsAddressEditing(false);
                  }}
                >
                  {t('common.cta.cancel')}
                </Button>
              </div>
            </form>
          </Card>
        ) : (
          <Card className={styles.cardContainer}>
            <AddressInfo
              info={addressPretty}
              onEditAddress={() => setIsAddressEditing(true)}
            />
          </Card>
        )}
      </div>
    </div>
  );
};

export const DeliveryAddressForm = () => {
  const { t } = useTranslation();
  const { address, setProp } = useDeliveryAddress();

  return (
    <fieldset name="delivery" style={{ border: '0rem' }}>
      <div className="flexAlign">
        <Field
          label={t('account-details.field.contactName.label')}
          name="name"
          setProp={(value) => setProp('name', value)}
          value={address['name'] || ''}
          isRequired
        />
        <Field
          label={t('account-details.field.phone.label')}
          name="phone"
          setProp={(value) => setProp('phone', value)}
          value={address['phone'] || ''}
          isRequired={true}
        />
      </div>
      <div className="flexAlign">
        <Field
          label={t('account-details.field.streetAddress.label')}
          name="line1"
          setProp={(value) => setProp('line1', value)}
          value={address['line1'] || ''}
          isRequired
        />
        <Field
          label={t('account-details.field.additions.label')}
          name="line2"
          setProp={(value) => setProp('line2', value)}
          value={address['line2'] || ''}
        />
      </div>
      <div className="flexAlign">
        <Field
          label={t('account-details.field.postalCode.label')}
          name="postalCode"
          setProp={(value) => setProp('postalCode', value)}
          value={address['postalCode'] || ''}
          isRequired
        />
        <Field
          label={t('account-details.field.city.label')}
          name="city"
          setProp={(value) => setProp('city', value)}
          value={address['city'] || ''}
          isRequired
        />
      </div>
      <AutocompleteCountryField
        label={t('account-details.field.country.label')}
        name="country"
        setProp={(value) => setProp('country', value)}
        countryCode={address.country as CountryCode}
        isRequired
      />
    </fieldset>
  );
};

const AutocompleteCountryField = ({
  label = '',
  name,
  isRequired = false,
  countryCode = 'NL',
  setProp,
}: {
  countryCode: CountryCode;
} & FieldProp) => {
  const { i18n } = useTranslation();

  const options = getCountriesList(i18n.resolvedLanguage).map((country) => ({
    value: country.code,
    label: country.label,
    selected: country.code === countryCode,
  }));

  return (
    <div className={styles.countryField}>
      <label htmlFor={name}>
        {label} {isRequired && '*'}
      </label>
      <Select
        id={name}
        required={isRequired}
        options={options}
        onChange={(e) => setProp(e.currentTarget.value)}
      />
    </div>
  );
};
